import React, { useRef, useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";

export async function uploadAvatar(file) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post("/uploads/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data.url;
}

// mode="immediate": uploads on select (needs auth). mode="deferred": returns File via onFile for later upload.
export function AvatarUpload({ value, onChange, onFile, mode = "immediate", testId = "avatar-upload" }) {
  const { t } = useI18n();
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(value || "");
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error(t("upload_type_err")); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error(t("upload_size_err")); return; }
    setPreview(URL.createObjectURL(file));
    if (mode === "deferred") { onFile?.(file); return; }
    setBusy(true);
    try { const url = await uploadAvatar(file); onChange?.(url); toast.success(t("upload_ok")); }
    catch (err) { toast.error(fmtErr(err.response?.data?.detail)); setPreview(value || ""); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-4">
      <button type="button" data-testid={testId} onClick={() => inputRef.current?.click()} disabled={busy}
        className="relative w-20 h-20 rounded-2xl overflow-hidden border border-stone-200 bg-stone-50 group shrink-0">
        {preview ? <img src={preview} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-stone-400"><Camera size={22} /></div>}
        <div className={`absolute inset-0 bg-stone-900/50 text-white flex items-center justify-center transition-opacity ${busy ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
          {busy ? <Loader2 size={20} className="animate-spin" /> : <Camera size={18} />}
        </div>
      </button>
      <div className="text-xs text-stone-500">
        <div className="font-medium text-stone-700 text-sm">{t("upload_photo")}</div>
        <div>{t("upload_hint")}</div>
      </div>
      <input ref={inputRef} data-testid={`${testId}-input`} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={pick} />
    </div>
  );
}
